import type { Metafield } from '@forecastify/shared';
import type { Cell, DataListColumn } from '@forecastify/ui';
import type { Translate } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A workspace's own fields, as list columns — item 27.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"An entity's metafields become optional columns."*
 *
 * ⚠️⚠️ **`inList` ALREADY EXISTED AND NOTHING READ IT.** `metafieldSchema` has carried
 * *"whether the field appears as a column in list views — the mockup's `list`"* since C10, the
 * settings screen has always offered the switch, and every list ignored it. So this is not a new
 * concept being invented; it is a promise the product has been making in its own settings screen
 * and not keeping.
 *
 * ⚠️ **Optional in the second sense too**: `DataList`'s toolbar already carries a column toggle
 * with its own `hidden` state, so a field somebody does not want to see is one click away without
 * touching the workspace's settings.
 *
 * ⚠️⚠️ **THEY DO NOT SORT, AND THAT IS DELIBERATE RATHER THAN UNFINISHED.** A sort key has to name
 * an expression in the server's catalogue (`sorting.ts`), and a metafield's value lives in a
 * `jsonb` column keyed by a name the workspace chose — so sorting one means ordering by
 * `(select value from metafield_values where …)`, a correlated subquery per row with no index
 * behind it. The columns are for reading and the FILTER is how they are narrowed.
 */
export function metafieldColumns(fields: readonly Metafield[]): DataListColumn[] {
  /* ⚠️ EVERY field is a column since ux-pass-2 — `inList` decides whether it starts SHOWN, and the
     rest wait in the column chooser. A field left out of the list could not be added at all. */
  return fields.map((field) => ({
    ...(field.inList ? {} : { defaultHidden: true }),
    /* ⚠️ Prefixed, because a metafield named `name` or `status` would otherwise collide with a
         real column's key and silently take its cell. `keySchema` allows both. */
    key: `mf.${field.key}`,
    /* ⚠️ NOT translated, and `metafields/contract.ts` says why: the name is DATA the workspace
         typed, not a string this product ships. Rule 7 is about our words. */
    label: field.name,
    width: field.type === 'textarea' ? 240 : 140,
    ...(field.type === 'number' ? { align: 'right' as const } : {}),
  }));
}

/**
 * The cells for those columns, from a row's own `metafields` map.
 *
 * ⚠️ **An em-dash for a field nobody filled in, muted.** An empty cell in a table of empty cells is
 * indistinguishable from a rendering failure, and `design-system/readme.md` reserves the em-dash
 * for exactly *"there is no value here"*.
 */
export function metafieldCells(
  t: Translate,
  fields: readonly Metafield[],
  values: Record<string, unknown>,
): Record<string, Cell> {
  const cells: Record<string, Cell> = {};

  for (const field of fields) {
    const value = values[field.key];
    cells[`mf.${field.key}`] = { text: render(t, field, value), muted: isBlank(value) };
  }

  return cells;
}

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * ⚠️⚠️ **ONE `t()` PER BRANCH WITH A LITERAL KEY.** `i18n:check` reads literal arguments only, so
 * `t(keyFor(type))` reports every entry as an orphan and the fix somebody reaches for is deleting
 * the words. `task-columns.ts` carries the same note over the same trap.
 */
function render(t: Translate, field: Metafield, value: unknown): string {
  if (isBlank(value)) return '–';

  /* ⚠️ **A checkbox is `Igen`/`Nem`, not `true`/`false`.** The wire carries a boolean and nobody
     reading a list is reading JSON. */
  if (field.type === 'checkbox') return value === true ? t('common.yes') : t('common.no');

  /* ⚠️ **`multi` is an ARRAY** — joined here rather than rendered as `[object Object]`, which is
     what `String()` on it produces and what a list would silently show. */
  if (Array.isArray(value)) return (value as unknown[]).map(scalar).join(', ');

  return scalar(value);
}

/**
 * ⚠️⚠️ **THE ALLOWED TYPES ARE LISTED RATHER THAN `String(value)` OVER `unknown`.** Every metafield
 * value crosses the wire as a string, a number or a boolean — but the field is typed `unknown`, and
 * `String()` on an object produces `[object Object]` in a table cell with nothing to explain it.
 * Naming the three makes anything else render as *"no value"*, which is the honest answer for a
 * shape this product does not produce.
 */
function scalar(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  return '–';
}

/**
 * The filter chips for a workspace's own fields — item 35's last piece.
 *
 * ⚠️⚠️ **ONLY `select` AND `checkbox`, AND THE REST ARE LEFT OUT DELIBERATELY.** `ListFilter` is a
 * drill-in menu of OPTIONS, and only a closed set has options to offer: a text or a number field
 * would need a typed value, which is what the search box already is for the columns that matter.
 * Offering an empty menu on a text field is a control that opens onto nothing.
 *
 * ⚠️ **The filter key carries the `mf.` prefix all the way to the server**, where
 * `metafieldFiltersFrom` reads it. `use-list-query.ts` sends every filter it holds verbatim, so the
 * prefix travelling is exactly what makes this need no special case in the middle.
 */
export function metafieldFilters(
  t: Translate,
  fields: readonly Metafield[],
  values: Readonly<Record<string, string>>,
  onChange: (key: string, value: string) => void,
): {
  key: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}[] {
  return fields
    .filter((field) => field.type === 'select' || field.type === 'checkbox')
    .map((field) => ({
      key: `mf.${field.key}`,
      label: field.name,
      value: values[`mf.${field.key}`] ?? '',
      options:
        field.type === 'checkbox'
          ? [
              { value: '', label: t('common.all') },
              /* ⚠️ **`'true'` and `'false'` as STRINGS**, because a query string has no booleans
                 and `''` already means *not filtered* — `use-list-query.ts` argues the third state. */
              { value: 'true', label: t('common.yes') },
              { value: 'false', label: t('common.no') },
            ]
          : [
              { value: '', label: t('common.all') },
              /* ⚠️ NOT translated: these are the workspace's own words. */
              ...field.options.map((option) => ({ value: option, label: option })),
            ],
      onChange: (value: string) => {
        onChange(`mf.${field.key}`, value);
      },
    }));
}
